import { useEffect, useState } from 'react';
import { ArrowRightIcon, MailIcon, MessageCircleIcon, PencilIcon, PhoneIcon } from 'lucide-react';
import { Panel } from '../ui/Panel';
import { ContactRow } from './ContactRow';
import { CreditPanel } from './CreditPanel';
import { BookingBlockPanel } from './BookingBlockPanel';
import { useAdmin } from '../AdminContext';
import { attentionFor, attentionLabel, attentionTab, whatsappNumber, type CustomerTab } from './customerRules';
import { btnGhost, btnPrimary, btnSecondary, inputCls } from '../ui/styles';
import { useCan } from '../useCan';
import { ContactEditForm } from './ContactEditForm';
import { effectivePlan } from '../../utils/account';
import { currentTip } from '../../utils/skinTips';
import { dayMedium } from '../../core/format/dates';
import { plural } from '../../core/format/text';
import { formatStreet, telHref } from '../../core/rules/contact';
import { acquisitionLabels } from '../../data/acquisition';
import type { CustomerFileControl } from './useCustomerFile';
import type { SourceAskedAt } from '../../core/types/insights';
type Props = {
  ctl: CustomerFileControl;
  onTab: (tab: CustomerTab) => void;
  onDirtyChange?: (dirty: boolean) => void;
};
const sourceVia: Record<SourceAskedAt, string> = {
  bestelling: 'na een bestelling',
  afspraak: 'bij het boeken',
  kassa: 'aan de kassa'
};

/** Eerste scherm van een dossier: wat er speelt, wat zij nu ziet, en hoe je haar bereikt. */
export function CustomerOverview({
  ctl,
  onTab,
  onDirtyChange
}: Props) {
  const {
    index,
    site
  } = useAdmin();
  const {
    account,
    internalNote
  } = ctl.file;
  const c = account.customer;
  const plan = effectivePlan(account, index.productById);
  const tip = currentTip(account, plan.items, index);
  const flags = attentionFor(account, site.policy.revisitAfterWeeks);
  const wa = whatsappNumber(c.phone);
  const [note, setNote] = useState(internalNote);
  useEffect(() => setNote(internalNote), [internalNote]);
  const noteDirty = note !== internalNote;
  useEffect(() => onDirtyChange?.(noteDirty), [noteDirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);
  const {
    canAll
  } = useCan();
  const [editingContact, setEditingContact] = useState(false);
  const prefs = [c.prefs.refillWhatsApp && 'seintje via WhatsApp', c.prefs.refillEmail && 'seintje per mail', c.prefs.newsletter && 'nieuwsbrief'].filter(Boolean);
  return <div className="grid gap-6 lg:grid-cols-3" data-id="element-1212" data-mp="2bmjj5wa6qqrv">
      <div className="min-w-0 space-y-6 lg:col-span-2" data-id="element-1213" data-mp="2bnchc8v2w7ye">
        {flags.length > 0 && <Panel title="Vraagt je aandacht" flush data-id="element-1214" data-mp="26oi19eohwxhz">
            <ul className="divide-y divide-line" data-id="element-1215" data-mp="26pt5tgprm9a5">
              {flags.map(f => <li key={f} data-id="element-1216" data-mp="2kl4x1e4pphmb">
                  <button type="button" onClick={() => onTab(attentionTab[f])} className="flex w-full items-center gap-3 px-4 py-3.5 text-left text-[14px] text-ink transition-colors duration-150 ease-soft hover:bg-shell sm:px-5" data-id="element-1217" data-mp="2glw58et8ftte">
                    <span className="h-2 w-2 shrink-0 rounded-full bg-clay" aria-hidden="true" data-id="element-1218" data-mp="3eenjn8a9e6g" />
                    <span className="flex-1" data-id="element-1219" data-mp="8ek9qnvsa8h5">{attentionLabel[f]}</span>
                    <ArrowRightIcon className="h-4 w-4 text-muted" strokeWidth={1.5} aria-hidden="true" data-id="element-1220" data-mp="8boc70nullt8" />
                  </button>
                </li>)}
            </ul>
          </Panel>}

        <Panel title={`Wat ${c.firstName} nu ziet in Mijn omgeving`} description="De tip van dit moment, zoals de site die kiest. De woorden pas je aan bij Paginateksten → Mijn omgeving." data-id="element-1221" data-mp="m5fje2e3wtls">
          <div className="rounded-md bg-ink p-5 text-shell" data-id="element-1222" data-mp="cg5uevek7r1w">
            <p className="font-display text-[20px] font-light leading-snug" data-id="element-1223" data-mp="cdjk4o0aaxfz">{tip.title}</p>
            <p className="mt-2 text-[14px] leading-relaxed text-shell/85" data-id="element-1224" data-mp="hcmglk277vcc">{tip.body}</p>
          </div>
          <p className="mt-4 text-[13px] text-muted" data-id="element-1225" data-mp="152732cxk9aqd">
            {plan.items.length === 0 ? 'Nog geen routine: ze ziet uitleg om te beginnen.' : plan.provisional ? `Voorlopige routine uit haar bestellingen (${plural(plan.items.length, 'product', 'producten')}). ` : `Persoonlijk huidplan met ${plural(plan.items.length, 'product', 'producten')}. `}
            {plan.items.length > 0 && <button type="button" onClick={() => onTab('huidplan')} className="text-clay underline underline-offset-4" data-id="element-1226" data-mp="33fli0wun12n4">
                {plan.provisional ? 'Maak het persoonlijk' : 'Huidplan bekijken'}
              </button>}
          </p>
        </Panel>

        <Panel title="Interne notitie" description={`Alleen voor jou. ${c.firstName} ziet dit nooit.`} data-id="element-1227" data-mp="3rcr7eis80hmk">
          <label className="sr-only" htmlFor="internal-note" data-id="element-1228" data-mp="3mdxtwqcxoqme">
            Interne notitie
          </label>
          <textarea id="internal-note" value={note} onChange={e => setNote(e.target.value)} rows={4} placeholder="Bijv. allergieën, zwangerschap, voorkeuren in de salon" className={`${inputCls} resize-y leading-relaxed`} data-id="element-1229" data-mp="no7j9catonl3" />
          <div className="mt-3 flex gap-2" data-id="element-1230" data-mp="v6f3xy9e419y">
            <button type="button" onClick={() => ctl.saveNote(note)} disabled={note === internalNote || ctl.isBusy('note')} aria-busy={ctl.isBusy('note')} className={btnPrimary} data-id="element-1231" data-mp="v7goctcaq04c">
              {ctl.isBusy('note') ? 'Bewaren…' : 'Notitie bewaren'}
            </button>
          </div>
        </Panel>
      </div>

      <div className="min-w-0 space-y-6" data-id="element-1232" data-mp="1a2d4dsqcjrrz">
        <Panel title="Contact" actions={canAll('customers.edit') && !editingContact ? <button type="button" onClick={() => setEditingContact(true)} className={btnGhost} data-id="element-1234" data-mp="1ixb35gg6dyr8">
                <PencilIcon className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" data-id="element-1235" data-mp="1nvclfz5s1u1n" />
                Bewerken
              </button> : null} data-id="element-1233" data-mp="1e59jvaklb7rp">
          {editingContact ? <ContactEditForm customer={c} onCancel={() => setEditingContact(false)} onSaved={() => {
          setEditingContact(false);
          ctl.reload();
        }} data-id="element-1236" data-mp="1xua6ut7wp7gf" /> : <>
          <dl className="space-y-3 text-[14px]" data-id="element-1237" data-mp="1hwrbm38fj5ro">
            <ContactRow label="E-mail" data-id="element-1238" data-mp="1hxiympnynv6c">
              {c.email ? <a href={`mailto:${c.email}`} className="break-all text-ink hover:text-clay" data-id="element-1239" data-mp="1d3eqw46vfj47">
                  {c.email}
                </a> : <span className="text-muted" data-id="element-1240" data-mp="1lxt503fo0nlq">Niet bekend · geen mails of inlog</span>}
            </ContactRow>
            <ContactRow label="Telefoon" data-id="element-1241" data-mp="20t91sg7mqxlt">{c.phone || <span className="text-muted" data-id="element-1242" data-mp="20s7n81renszc">Niet bekend</span>}</ContactRow>
            <ContactRow label="Adres" data-id="element-1243" data-mp="1vw0jmfmhetkk">
              {c.address.street ? <>
                  {formatStreet(c.address)}
                  <br data-id="element-1244" data-mp="1hpqhuyutd8z" />
                  {c.address.postcode} {c.address.city}
                  {c.address.country === 'BE' && ' · België'}
                </> : <span className="text-muted" data-id="element-1245" data-mp="2d75pjfhfeabq">Niet bekend</span>}
            </ContactRow>
            <ContactRow label="Klant sinds" data-id="element-1246" data-mp="2i5pbvgzxlo4m">{dayMedium(c.memberSince)}</ContactRow>
            <ContactRow label="Kwam via" data-id="element-1247" data-mp="23cvx5xshhc10">
              {c.acquisitionSource ? <>
                  {acquisitionLabels[c.acquisitionSource.channel]}
                  {c.acquisitionSource.other && `: ${c.acquisitionSource.other}`}
                  <span className="block text-[12px] text-muted" data-id="element-1248" data-mp="1yg5h3agoigfl">
                    Gezegd {sourceVia[c.acquisitionSource.via]}, {dayMedium(c.acquisitionSource.at)}
                  </span>
                </> : <span className="text-muted" data-id="element-1249" data-mp="1pfqso28f4l7v">Niet bekend</span>}
            </ContactRow>
            <ContactRow label="Betaalt met" data-id="element-1250" data-mp="3euoplbjabi47">{c.payment.label}</ContactRow>
            <ContactRow label="Wil graag" data-id="element-1251" data-mp="3js6zb454m1d9">{prefs.length ? prefs.join(', ') : <span className="text-muted" data-id="element-1252" data-mp="26mdm4sofmvw2">Geen berichten</span>}</ContactRow>
          </dl>
          <div className="mt-5 grid grid-cols-2 gap-2 border-t border-line pt-4" data-id="element-1253" data-mp="22ks4yv4v7vg2">
            {wa && <a href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer" className={btnPrimary} data-id="element-1254" data-mp="1xu3d3ep9wwge">
                <MessageCircleIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-1255" data-mp="1sv8kjl1i7tj3" />
                WhatsApp
                <span className="sr-only" data-id="element-1256" data-mp="1nw5qswqz59qf"> met {c.firstName} (opent in een nieuw tabblad)</span>
              </a>}
            {c.phone && !wa && <a href={telHref(c.phone)} className={btnSecondary} data-id="element-1257" data-mp="1iz5isgakdwwo">
                <PhoneIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-1258" data-mp="1evr2doipreip" />
                Bellen
              </a>}
            {c.email && <a href={`mailto:${c.email}`} className={btnSecondary} data-id="element-1259" data-mp="399i9ohdklmaz">
                <MailIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-1260" data-mp="34a5n7xqm2h8x" />
                Mail
              </a>}
          </div>
          </>}
        </Panel>
        <CreditPanel data-id="element-1261" data-mp="3gsrjuctwhp13" />
        <BookingBlockPanel ctl={ctl} data-id="element-1262" data-mp="3gs7z6ocv8mjd" />
        {/* Wie het dossier hiervoor opende (het logboek): zo zie je of iemand anders erin keek. */}
        {ctl.file.lastViewed && <p className="px-1 text-[12px] text-muted" data-id="element-1263" data-mp="3qikunbm7y04a">
            Laatst ingezien door {ctl.file.lastViewed.name}, {dayMedium(ctl.file.lastViewed.at.slice(0, 10))} om {ctl.file.lastViewed.at.slice(11, 16)}
          </p>}
      </div>
    </div>;
}